import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ModalBody } from 'reactstrap';
import { CancelButton } from 'src/components/Buttons';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { INVENTORY_BULK_CREATE_LISTING_GROUPS_KEY } from 'src/components/Listings/InventoryActionDropdown';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditListingClient,
  Event,
  RowGroupSetting,
  VenueZoneConfigType,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../common';
import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from '../common/EventEntityHeader';
import { Summary } from '../common/Summary';
import { ModalFooter } from '../Modal';
import { ModalBodyDataContainer } from '../Modal/Modal.styled';
import { GroupsPreview } from './components/GroupsPreview';
import { UngroupedListingGroupId } from './components/GroupsPreview/GroupListingsPreviewTable.constants';
import { GroupListingsBody } from './GroupListingsBody';
import { useGroupListingsAction } from './GroupListingsV2.hooks';
import {
  GroupListingsFormProps,
  GroupListingsState,
  GroupSortByType,
  ListingGroupBy,
  ListingGroupType,
} from './GroupListingsV2.types';

type GroupListingsV2Props = {
  event: Event;
  multiSelectEvents?: Event[];
};

const DEFAULT_ZONE_MAP_RANGES = [
  { min: 1, max: 1 },
  { min: 2, max: 5 },
  { min: 6, max: Number.MAX_VALUE },
];

/** Maps DB sort IDs to GroupSortByType enum values */
const SORT_ID_TO_TYPE: Record<number, GroupSortByType> = {
  1: GroupSortByType.RowDesc,
  2: GroupSortByType.RowAsc,
  3: GroupSortByType.SectionDesc,
  4: GroupSortByType.SectionAsc,
  5: GroupSortByType.UnsoldQuantityDesc,
  6: GroupSortByType.UnsoldQuantityAsc,
};

/** Parses a row splits string like "1-1,2-5,6+" into GroupRange[] */
const parseRowSplits = (rowSplitsStr: string | null | undefined) => {
  if (!rowSplitsStr) return DEFAULT_ZONE_MAP_RANGES;
  try {
    return rowSplitsStr.split(',').map((part) => {
      if (part.endsWith('+')) {
        return { min: parseInt(part), max: Number.MAX_VALUE };
      }
      const [min, max] = part.split('-').map(Number);
      return { min, max: max ?? min };
    });
  } catch {
    return DEFAULT_ZONE_MAP_RANGES;
  }
};

export const GroupListingsV2: React.FC<GroupListingsV2Props> = ({
  event,
  multiSelectEvents,
}) => {
  const { data: accountDefaults, isFetched: isAccountDefaultsFetched } =
    useListingGroupSellerAccountSettings();

  const defaultValues = useMemo((): GroupListingsFormProps => {
    const baseDefaults: GroupListingsFormProps = {
      venueZoneConfigType: VenueZoneConfigType.ConcertsSectionsBehindStage,
      rowGroupSetting: RowGroupSetting.SingleTwoToFiveSixPlus,
      desiredActiveListings: null,
      deprioritizedQuantities: [],
      groupUndercutSettings: null,
      marketplaceSettings: null,
      groupBys: [
        {
          groupingType: ListingGroupType.ZoneMap,
          ranges: DEFAULT_ZONE_MAP_RANGES,
        },
      ],
      mergeListingGroupInputs: [],
      groupSortBy: null,
    };

    if (!accountDefaults) {
      return baseDefaults;
    }

    // Apply account defaults
    const result = { ...baseDefaults };

    // Default active listings
    if (accountDefaults.desiredActiveListings != null) {
      result.desiredActiveListings = accountDefaults.desiredActiveListings;
    }

    // Default pricing settings (rank premium)
    if (accountDefaults.pricingSettingsJson) {
      try {
        result.groupUndercutSettings = JSON.parse(
          accountDefaults.pricingSettingsJson
        );
      } catch {
        // ignore parse errors
      }
    }

    // Default deprioritized quantities
    if (accountDefaults.deprioritizedQuantities) {
      result.deprioritizedQuantities = accountDefaults.deprioritizedQuantities
        .split(',')
        .filter(Boolean);
    }

    // Default group bys (section split type)
    if (accountDefaults.defaultSectionSplitTypeId != null) {
      const splitTypeId = accountDefaults.defaultSectionSplitTypeId;
      let groupBys: ListingGroupBy[];
      if (splitTypeId === 1) {
        groupBys = [
          {
            groupingType: ListingGroupType.ZoneMap,
            ranges: parseRowSplits(accountDefaults.defaultRowSplits),
          },
        ];
      } else if (splitTypeId === 2) {
        groupBys = [{ groupingType: ListingGroupType.Section }];
      } else if (splitTypeId === 3) {
        groupBys = [{ groupingType: ListingGroupType.Zone }];
      } else {
        groupBys = baseDefaults.groupBys;
      }
      result.groupBys = groupBys;
    }

    // Default sort order
    if (accountDefaults.defaultSortId != null) {
      result.groupSortBy =
        SORT_ID_TO_TYPE[accountDefaults.defaultSortId] ?? null;
    }

    return result;
  }, [accountDefaults]);

  // Wait for account defaults to load before rendering the form
  // to avoid resetting the form after defaults load
  if (!isAccountDefaultsFetched) {
    return null;
  }

  return (
    <GroupListingsV2WithDefaults
      event={event}
      multiSelectEvents={multiSelectEvents}
      defaultValues={defaultValues}
    />
  );
};

type GroupListingsV2WithDefaultsProps = GroupListingsV2Props & {
  defaultValues: GroupListingsFormProps;
};

const GroupListingsV2WithDefaults: React.FC<
  GroupListingsV2WithDefaultsProps
> = ({ event, multiSelectEvents, defaultValues }) => {
  const methods = useForm<GroupListingsFormProps>({
    defaultValues,
  });
  return (
    <FormProvider {...methods}>
      <EventMapContextProvider event={event}>
        <GroupListingV2Content
          event={event}
          multiSelectEvents={multiSelectEvents}
        />
      </EventMapContextProvider>
    </FormProvider>
  );
};

const GroupListingV2Content: React.FC<GroupListingsV2Props> = ({
  event,
  multiSelectEvents,
}) => {
  const { trackError } = useClientLogContext();

  const { formState, reset, watch } = useFormContext<GroupListingsFormProps>();
  const [isLoading, setIsLoading] = useState(false);
  const { isSubmitting } = formState;
  const { setActivePosEntity } = useActivePosEntityContext();
  const { setProgress, setPreview, setStage, setMainDialogOpened } =
    useBulkEditHubContext();

  const { closeModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { bulkEditHub, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.GroupListings,
    INVENTORY_BULK_CREATE_LISTING_GROUPS_KEY,
    async () => {
      setActivePosEntity(0);
      await refreshExpandedListItems();

      closeModal();
    }
  );
  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const groups = watch('mergeListingGroupInputs');

  useEffect(() => {
    initJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = useCallback(
    (inputFormData: GroupListingsFormProps) => {
      setIsLoading(true);

      tryInvokeApi(
        async () => {
          const filteredListingGroupInputs =
            inputFormData.mergeListingGroupInputs
              .map((groupInput) => ({
                ...groupInput,
                desiredActiveListings: groupInput.desiredActiveListings ?? 0,
                listingGroupItems: groupInput.listingGroupItems.filter(
                  (x) => x.listingId > 0
                ),
              }))
              .filter(
                (m) =>
                  m.listingGroupItems.length &&
                  m.listingGroupId !== UngroupedListingGroupId
              );

          const succeeded = await new BulkEditListingClient(
            activeAccountWebClientConfig
          ).bulkCreateListingGroups(
            {
              mergeListingGroupInputs: filteredListingGroupInputs,
            },
            INVENTORY_BULK_CREATE_LISTING_GROUPS_KEY,
            hasBackgroundBulkEditFeature
          );

          reset(inputFormData);
          if (!hasBackgroundBulkEditFeature) {
            if (succeeded) {
              setActivePosEntity(0);
              await refreshExpandedListItems();

              closeModal();
            }
          }
        },
        (error) => {
          trackError('BulkEditListingClient.bulkCreateListingGroups', error, {
            trackErrorData: inputFormData,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      closeModal,
      hasBackgroundBulkEditFeature,
      refreshExpandedListItems,
      reset,
      setActivePosEntity,
      trackError,
    ]
  );
  const { state, onFooterNav, eventListings } = useGroupListingsAction(
    event,
    onSubmit,
    multiSelectEvents,
    () => setIsLoading(true)
  );

  return (
    <>
      <CancellableFormHeader
        disabled={isLoading || isSubmitting}
        onBeforeClose={() => {
          setProgress(undefined);
          setPreview(undefined);
          setStage(BulkEditStage.Idle);
          setMainDialogOpened(false);
        }}
      >
        <ConnectedEventEntityHeader
          title={<Content id={ContentId.GroupListings} />}
        />
      </CancellableFormHeader>

      <ModalBody>
        <ModalBodyDataContainer>
          {multiSelectEvents == null && (
            <div style={{ paddingBottom: vars.spacing.sm }}>
              <Summary event={event!} />
            </div>
          )}
          <BulkEditStatus
            entityType={ActionOutboxEntityType.Listing}
            updateKey={INVENTORY_BULK_CREATE_LISTING_GROUPS_KEY}
          >
            {state === GroupListingsState.Review ? (
              <GroupsPreview eventListings={eventListings} groups={groups} />
            ) : (
              <GroupListingsBody event={event} />
            )}
          </BulkEditStatus>
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack width="full" direction="row" justifyContent="spaceBetween">
          {state === GroupListingsState.Configure && (
            <CancellableFormFooter
              disabled={isLoading || isSubmitting}
              onBeforeClose={() => {
                setProgress(undefined);
                setPreview(undefined);
                setStage(BulkEditStage.Idle);
                setMainDialogOpened(false);
              }}
            />
          )}
          {state === GroupListingsState.Review && (
            <CancelButton
              textContentId={ContentId.Back}
              onClick={() => onFooterNav(true)}
            />
          )}
          <Button disabled={isLoading} onClick={() => onFooterNav()}>
            {state === GroupListingsState.Configure && !multiSelectEvents ? (
              <Content id={ContentId.Next} />
            ) : (
              <Content id={ContentId.Save} />
            )}
          </Button>
        </Stack>
      </ModalFooter>
    </>
  );
};
